import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { selectUserToken } from "../../../../../features/userSlice";
import { familiesEndPointLogic } from "../../../../../logic/DataCollection/Families/FamiliesEndPointLogic";
import { familiesServices } from "../../../../../services/api/families.endpoints";
import type { FamilyItem } from "./NeedAttentionFamiliesAction";
import { useWebsiteSharedUid } from "../../../../../customHooks/useWebsiteSharedUid";

interface Props {
  open: boolean;
  handleClose: () => void;
  item: FamilyItem | null;
  onDeleted?: () => void;
}

const DeleteFamilyModal = ({ open, handleClose, item, onDeleted }: Props) => {
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  // Used only to fetch Q1 below — the DELETE URL itself now takes the
  // family's own shared_uid, resolved from that same Q1 response.
  const website_shared_uid = useWebsiteSharedUid();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [familySharedUid, setFamilySharedUid] = useState<string | number | null>(null);

  // This modal has no Q1 data of its own — resolve the family's real
  // shared_uid (family_shared_uid) here before Delete can be clicked.
  useEffect(() => {
    if (!open || !item) return;
    let cancelled = false;
    setIsResolving(true);
    setFamilySharedUid(null);
    familiesServices
      .getFamiliesOverview(oauthTokenHeader, website_shared_uid, item.backendInterfaceId)
      .then((response) => {
        if (cancelled) return;
        const rows = response?.data?.data?.query_output ?? [];
        const match = rows.find(
          (row: any) => row.family_id === item.id && row.step === item.step,
        );
        if (match) setFamilySharedUid(match.family_shared_uid ?? null);
      })
      .finally(() => {
        if (!cancelled) setIsResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, item, oauthTokenHeader, website_shared_uid]);

  const handleDelete = async () => {
    if (!item || !familySharedUid) return;
    setIsSubmitting(true);
    const success = await familiesEndPointLogic.deleteFamily(
      oauthTokenHeader,
      familySharedUid,
      { step: item.step, familyId: item.id },
      dispatch,
    );
    setIsSubmitting(false);
    if (success) {
      handleClose();
      onDeleted?.();
    }
  };

  if (!item) return null;

  const stepTitle = item.step.charAt(0).toUpperCase() + item.step.slice(1);
  const hasBrands = item.brands > 0;
  const thingsLabel = item.step === "fetcher" ? "config" : "map";

  return (
    <Dialog open={open} PaperProps={{ sx: { width: "480px", maxWidth: "480px" } }}>
      <Paper sx={{ padding: "16px 24px 20px 24px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <DialogTitle sx={{ padding: "0px 0px 4px 0px" }}>
              Delete family &quot;{item.name}&quot;
            </DialogTitle>
            <Typography variant="body2" color="#00000099" sx={{ px: 3, mt: "-8px" }}>
              {item.provider} · {stepTitle}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            mt: 2,
            border: "1px solid #0288D180",
            borderRadius: "5px",
            backgroundColor: "#f4faff",
            p: 1.5,
          }}
        >
          {hasBrands ? (
            <>
              <Typography sx={{ fontSize: 14, color: "#000000DE" }}>
                This family has <b>{item.brands}</b>{" "}
                {item.brands === 1 ? "connected brand" : "connected brands"}. Deleting
                removes links only — brand, map/config and history rows are never
                deleted.
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#00000099", mt: 1 }}>
                The {item.brands} {item.brands === 1 ? "brand" : "brands"} become
                unassigned for the {stepTitle} step until the system rematches{" "}
                {item.brands === 1 ? "it" : "them"}.
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 14, color: "#000000DE" }}>
              No brands are connected. This removes the family and its{" "}
              {item.configs} {thingsLabel}
              {item.configs === 1 ? "" : "s"} link{item.configs === 1 ? "" : "s"} — no
              brand, map/config or history rows are deleted.
            </Typography>
          )}
        </Box>

        <Stack flexDirection="row" justifyContent="space-between" marginTop={2}>
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isSubmitting || isResolving || !familySharedUid}
            startIcon={
              isSubmitting || isResolving ? <CircularProgress size={14} color="inherit" /> : undefined
            }
          >
            Delete family
          </Button>
        </Stack>
      </Paper>
    </Dialog>
  );
};

export default DeleteFamilyModal;
